export type GTMEvent = { event?: string } & Record<string, unknown>

export type PageViewMetadata = {
    page_lifecycle?: string
    page_type?: string
    product_type?: string
    category?: string
}

export type PageViewPayload = {
    page_location: string
    page_path: string
    page_title: string
    page_type: string
}

type Transport = (event: GTMEvent) => void

type Logger = {
    warn: (message: string, ...args: unknown[]) => void
}

type Options = {
    /**
     * How long queued events wait for a page to be started before they are released
     * without a page view. A safety net for trees where the tracker never mounts.
     */
    orphanTimeoutMs?: number
    logger?: Logger
}

export const PAGE_VIEW_EVENT = "page_view"
export const PAGE_VIEW_METADATA_SCRIPT_ID = "page-view-metadata"

export const serializePageViewMetadata = (metadata: PageViewMetadata) =>
    JSON.stringify(metadata).replace(/</g, "\\u003c")

export const readPageViewMetadataScript = (): PageViewMetadata => {
    if (typeof document === "undefined") return {}

    const el = document.getElementById(PAGE_VIEW_METADATA_SCRIPT_ID)
    if (!el?.textContent) return {}

    try {
        return JSON.parse(el.textContent) as PageViewMetadata
    } catch {
        return {}
    }
}

const DEFAULT_ORPHAN_TIMEOUT_MS = 5000

/**
 * Serialises analytics so that, for every page, `page_view` is the first event GTM
 * receives and every other event follows it in the order it was requested.
 *
 * Lifecycle per page:
 *   1. `startPage(key, payload, metadata?)` — called by the layout tracker on every
 *      pathname change. Metadata comes from the `PageViewMetadata` JSON script tag the
 *      page rendered (falls back to metadata staged via `setPageMetadata`). Sends
 *      `page_view` (base payload + metadata) and releases the queue.
 *   2. `dispatch(event)` — any other event. Sent immediately once the page view for the
 *      current page is out; held otherwise.
 *
 * `startPage` with the same key as the current page is a no-op, which is what prevents a
 * duplicate page view for the same page.
 */
export const createPageViewGate = (transport: Transport, options: Options = {}) => {
    const orphanTimeoutMs = options.orphanTimeoutMs ?? DEFAULT_ORPHAN_TIMEOUT_MS
    const logger = options.logger ?? console

    let currentPageKey: string | null = null
    let pageViewSent = false
    let previousPageLocation: string | null = null
    let stagedMetadata: PageViewMetadata | null = null
    let queue: GTMEvent[] = []
    let orphanTimer: ReturnType<typeof setTimeout> | null = null

    const clearOrphanTimer = () => {
        if (orphanTimer === null) return

        clearTimeout(orphanTimer)
        orphanTimer = null
    }

    const drainQueue = () => {
        const pending = queue
        queue = []
        // forEach would also pass (index, array); the transport takes only the event.
        pending.forEach(event => transport(event))
    }

    const releaseOrphanedEvents = () => {
        orphanTimer = null

        if (pageViewSent || queue.length === 0) return

        logger.warn(
            "[tracking] releasing queued events without a page view — no page was started",
            { count: queue.length }
        )
        pageViewSent = true
        drainQueue()
    }

    const dispatch = (event: GTMEvent) => {
        if (pageViewSent) {
            transport(event)
            return
        }

        queue.push(event)

        if (orphanTimer === null) {
            orphanTimer = setTimeout(releaseOrphanedEvents, orphanTimeoutMs)
        }
    }

    const setPageMetadata = (metadata: PageViewMetadata) => {
        stagedMetadata = metadata
    }

    const startPage = (
        key: string,
        payload: PageViewPayload,
        metadata?: PageViewMetadata
    ) => {
        if (key === currentPageKey) return

        clearOrphanTimer()
        currentPageKey = key
        pageViewSent = false

        const resolvedMetadata = metadata ?? stagedMetadata ?? {}
        stagedMetadata = null

        const pageReferrer =
            previousPageLocation ??
            (typeof document === "undefined" ? "" : document.referrer)

        transport({
            event: PAGE_VIEW_EVENT,
            ...payload,
            ...resolvedMetadata,
            page_referrer: pageReferrer
        })

        previousPageLocation = payload.page_location
        pageViewSent = true
        drainQueue()
    }

    const reset = () => {
        clearOrphanTimer()
        currentPageKey = null
        pageViewSent = false
        previousPageLocation = null
        stagedMetadata = null
        queue = []
    }

    return {
        dispatch,
        setPageMetadata,
        startPage,
        reset,
        getState: () => ({
            currentPageKey,
            pageViewSent,
            queuedCount: queue.length,
            hasStagedMetadata: stagedMetadata !== null
        })
    }
}

export type PageViewGate = ReturnType<typeof createPageViewGate>
